import { ComponentProps, ComponentType, forwardRef, SVGProps, useState } from 'react';

import { mergeClasses } from '@/utils';

import { Label, SvgIcon } from '@/components/common';
import { EyeIcon, EyeOffIcon, FilledCheckCircleIcon } from '@/assets/icons';

import {
  ICON_VARIANTS,
  INPUT_POSTFIX_PADDING_VARIANTS,
  INPUT_PREFIX_PADDING_VARIANTS,
  INPUT_VARIANTS,
  LABEL_VARIANTS,
} from './Input.constants';

export interface IInput extends Omit<ComponentProps<'input'>, 'prefix' | 'postfix' | 'size'> {
  prefix?: ComponentType<SVGProps<SVGSVGElement>>;
  postfix?: ComponentType<SVGProps<SVGSVGElement>>;
  prefixStyle?: string;
  postfixStyle?: string;
  label?: string;
  error?: string;
  status?: 'success' | 'error';
  size?: 'sm' | 'default' | 'lg';
  hasSuccessIcon?: boolean;
}

const Input = forwardRef<HTMLInputElement, IInput>(
  (
    {
      className,
      type,
      prefix,
      postfix,
      prefixStyle,
      postfixStyle,
      label,
      error,
      status,
      required,
      size = 'default',
      disabled,
      hasSuccessIcon = false,
      ...props
    },
    ref,
  ) => {
    const [passwordVisible, togglePassword] = useState(false);

    return (
      <div className={mergeClasses('flex flex-col gap-2 w-full', className)}>
        {label && (
          <Label required={required} className={mergeClasses(LABEL_VARIANTS({ size }))}>
            {label}
          </Label>
        )}

        <div className="relative flex items-center gap-3">
          {prefix && (
            <div
              className={mergeClasses(
                'pointer-events-none absolute left-4 flex items-center text-neutral-9 disabled:text-neutral-6 dark:text-neutral-0 disabled:dark:text-neutral-7',
                prefixStyle,
              )}
            >
              <SvgIcon component={prefix} className={ICON_VARIANTS({ size })} />
            </div>
          )}

          <input
            ref={ref}
            type={type === 'password' ? (passwordVisible ? 'text' : 'password') : type}
            required={required}
            disabled={disabled}
            data-slot="input"
            data-error={!!error || status === 'error'}
            className={mergeClasses(
              INPUT_VARIANTS({
                size,
                variant: error || status === 'error' ? 'error' : undefined,
                disabled,
              }),
              prefix && INPUT_PREFIX_PADDING_VARIANTS({ size }),
              postfix || (type === 'password' && INPUT_POSTFIX_PADDING_VARIANTS({ size })),
              status === 'success' && '!border-semantic-success-base',
              hasSuccessIcon && type === 'password' && '!pr-[74px]',
              type === 'password' && 'truncate',
            )}
            {...props}
          />

          {postfix && (
            <div
              className={mergeClasses(
                'pointer-events-none absolute right-4 flex items-center text-neutral-9 disabled:text-neutral-6 dark:text-neutral-0 disabled:dark:text-neutral-7',
                postfixStyle,
              )}
            >
              <SvgIcon component={postfix} className={ICON_VARIANTS({ size })} />
            </div>
          )}

          {(hasSuccessIcon || type === 'password') && (
            <div className="absolute right-4 flex items-center gap-3">
              {hasSuccessIcon && (
                <div className="pointer-events-none flex items-center text-neutral-9 disabled:text-neutral-6 dark:text-neutral-0 disabled:dark:text-neutral-7">
                  <SvgIcon component={FilledCheckCircleIcon} className="size-4" />
                </div>
              )}
              {type === 'password' && (
                <button
                  type="button"
                  onClick={() => togglePassword(!passwordVisible)}
                  className="flex items-center justify-center"
                  aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                >
                  <SvgIcon
                    component={passwordVisible ? EyeOffIcon : EyeIcon}
                    className="text-neutral-0 w-5 h-5"
                  />
                </button>
              )}
            </div>
          )}
        </div>

        {error?.trim() && <p className="text-body-xs leading-5 text-red-base">{error}</p>}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
